import React from 'react';
import styled from 'styled-components';
import SparkLogo from './SparkLogo';
import { useTranslation } from '../context/LanguageContext';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from "@phosphor-icons/react";

const TryBanner = styled.div`
  background: rgba(13, 17, 23, 0.75);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: 1px solid rgba(58, 183, 255, 0.2);
  border-radius: 24px;
  padding: 2rem;
  margin: -6rem auto 2rem;
  max-width: 1200px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.2),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);

  h2 {
    font-size: 1.5rem;
    background: linear-gradient(135deg, #3AB7FF 0%, #2EA3E5 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    font-weight: 600;
  }

  p {
    color: #8b949e;
    margin: 0.5rem 0 0;
  }

  @media (max-width: 768px) {
    margin: -4rem auto 2rem;
    padding: 1.5rem;
  }
`;

const GetStartedButton = styled(Link)`
  background: rgba(58, 183, 255, 0.9);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: #0A1628;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(58, 183, 255, 1);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(58, 183, 255, 0.25);
  }

  svg {
    width: 20px;
    height: 20px;
    color: #0A1628;
  }
`;

const FooterContainer = styled.footer`
  background: transparent;
  color: white;
  padding: 0 2rem 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem 1.5rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 2fr repeat(3, 1fr);
  gap: 3rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FooterBrand = styled.div`
  .logo {
    margin-bottom: 1rem;
  }

  p {
    color: #8b949e;
    line-height: 1.6;
    font-size: 0.9rem;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 1rem;
    margin-bottom: 1rem;
    color: white;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 0.5rem;

      a {
        color: #8b949e;
        text-decoration: none;
        font-size: 0.9rem;
        transition: color 0.2s;

        &:hover {
          color: #3AB7FF;
        }
      }
    }
  }
`;

const FooterBottom = styled.div`
  max-width: 1200px;
  margin: 2rem auto 0;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #8b949e;
  font-size: 0.9rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  padding: 0.25rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  button {
    background: transparent;
    border: none;
    color: #8b949e;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      color: white;
      background: rgba(255, 255, 255, 0.1);
    }

    &.active {
      color: white;
      background: rgba(58, 183, 255, 0.15);
      border: 1px solid rgba(58, 183, 255, 0.3);
    }
  }
`;

const Footer: React.FC = () => {
  const { t, language, switchLanguage } = useTranslation();
  const location = useLocation();

  return (
    <FooterContainer>
      {location.pathname !== '/beta' && (
        <TryBanner>
          <div>
            <h2>Try Spark Today</h2>
            <p>Start for free, then grow with your team.</p>
          </div>
          <GetStartedButton to="/beta">
            Get Started
            <ArrowRight weight="bold" />
          </GetStartedButton>
        </TryBanner>
      )}
      <FooterContent>
        <FooterBrand>
          <div className="logo">
            <SparkLogo />
          </div>
          <p>{t('The most intelligent platform for schools')}</p>
        </FooterBrand>

        <FooterColumn>
          <h3>{t('Resources')}</h3>
          <ul>
            <li><a href="/blog">{t('Blog')}</a></li>
            <li><a href="/community">{t('Community')}</a></li>
            <li><a href="/help">{t('Help Center')}</a></li>
          </ul>
        </FooterColumn>

        <FooterColumn>
          <h3>{t('Products')}</h3>
          <ul>
            <li><a href="/homeschool">{t('For Homeschooling')}</a></li>
            <li><a href="/pricing">{t('Pricing')}</a></li>
            <li><a href="/faq">{t('FAQ')}</a></li>
          </ul>
        </FooterColumn>

        <FooterColumn>
          <h3>{t('Legal')}</h3>
          <ul>
            <li><a href="/privacy">{t('Privacy & Security')}</a></li>
            <li><a href="/terms">{t('Terms')}</a></li>
            <li><a href="/accessibility">{t('Accessibility')}</a></li>
          </ul>
        </FooterColumn>
      </FooterContent>

      <FooterBottom>
        <LanguageSwitcher>
          <button
            className={language === 'en' ? 'active' : ''}
            onClick={() => switchLanguage('en')}
          >
            English
          </button>
          <button
            className={language === 'ar' ? 'active' : ''}
            onClick={() => switchLanguage('ar')}
          >
            العربية
          </button>
        </LanguageSwitcher>
        <span>{t('SparkSkool is a registered trademark of LeapTheLimit Inc. © 2023-2025. All rights reserved.')}</span>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;
